import {
    IItem,
    IProps,
} from './types';

import classNames from 'classnames';
import React, {
    CSSProperties,
    FC,
    useCallback,
    useMemo,
} from 'react';

import {
    useIsMobile,
} from 'helpers/adaptive';

import {
    CheckboxInput,
} from 'components/inputs';

import Loading from '../Loading';

import BulkActionBar from './BulkActionBar';
import EmptyResult from './EmptyResult';
import DataCell from './Row/DataCell';
import RowsWrapper from './RowsWrapper';

import {
    formatGridTemplateColumns,
} from './functions';
import {
    useShiftDragSelection,
} from './hooks';

import styles from './EntityTable.module.scss';

const EntityTable: FC<IProps<IItem>> = (props) => {
    const isMobile = useIsMobile();

    const {
        onRowMouseDown,
        onRowMouseEnter,
        onToggleId: onToggleShiftId,
    } = useShiftDragSelection(
        props.bulk,
        props.items
    );

    const columns = useMemo(() => {
        if (isMobile) {
            return props.columnsMobile;
        }

        return props.columnsDesktop;
    }, [isMobile, props.columnsMobile, props.columnsDesktop]);

    const gridTemplateColumns = useMemo(() => {
        return formatGridTemplateColumns({
            gridColumns: columns
                .map((column) => {
                    const option = props.formatColumnOption(column, isMobile);

                    if (option.span) {
                        return `${option.span}fr`;
                    }
                    if (option.width) {
                        return `${option.width}px`;
                    }

                    return '0px';
                }),
            isMobile,
            maxRowWidthMobile: props.maxRowWidthMobile,
            maxRowWidthDesktop: props.maxRowWidthDesktop,
        });
    }, [columns, isMobile, props.maxRowWidthMobile, props.maxRowWidthDesktop]);

    const Row = useCallback((newProps: any) => {
        return (<props.row {...newProps}/>);
    }, []);

    const onToggleAll = () => {
        if (!props.bulk) {
            return;
        }
        if (!props.items) {
            return;
        }
        if (props.bulk.selectedIds.length) {
            props.bulk.setSelectedIds([]);

            return;
        }

        const allIds = props.items.map((item) => item.id);

        props.bulk.setSelectedIds(allIds);
    };

    const gridStyle: CSSProperties = {
        display: 'grid',
        gridTemplateColumns,
    };

    const onToggleId = (id: string | number) => {
        if (!props.bulk) {
            return;
        }
        if (props.bulk.selectedIds.includes(id)) {
            props.bulk.setSelectedIds((selectedIds) => {
                return selectedIds.filter((selectedId) => selectedId !== id);
            });

            onToggleShiftId(id);

            return;
        }

        props.bulk.setSelectedIds((selectedIds) => {
            return [...selectedIds, id];
        });

        onToggleShiftId(id);
    };

    return (
        <div className={classNames(styles.entityTable, props.className)}>
            <div className={classNames(styles.head, styles.row)}>
                {
                    props.bulk?.isActiveIdsSelecting &&
                    <div className={styles.selectInput}>
                        <CheckboxInput
                            value={props.items?.length === props.bulk.selectedIds.length}
                            onChangeValue={onToggleAll}
                        />
                    </div>
                }
                <div
                    className={styles.headGrid}
                    style={gridStyle}
                >
                    {[
                        ...columns
                            .map((column) => {
                                const option = props.formatColumnOption(column, isMobile);

                                return (
                                    <DataCell
                                        key={column}
                                        className={classNames(styles.cell, styles.headCell)}
                                        isMobile={isMobile}
                                        option={{
                                            ...option,
                                            isBold: false,
                                        }}
                                        maxRowWidthDesktop={props.maxRowWidthDesktop ?? null}
                                        maxRowWidthMobile={props.maxRowWidthMobile ?? null}
                                    >
                                        {option.title}
                                    </DataCell>
                                );
                            }),
                    ]}
                </div>
                {
                    props.additionalContentClassName &&
                    <div className={props.additionalContentClassName}/>
                }
                <div
                    key={'options'}
                    className={styles.optionsFiller}
                />
            </div>
            {
                props.items ?
                    props.items.length ?
                        <RowsWrapper infiniteScroll={props.infiniteScroll ?? null}>
                            {
                                props.items.map((item, rowIndex) => {
                                    const isSelected = !!props.bulk?.isActiveIdsSelecting && props.bulk.selectedIds.includes(item.id);

                                    return (
                                        <div
                                            key={item.id}
                                            className={classNames(
                                                styles.row,
                                                isSelected && styles.selectedRow
                                            )}
                                            onMouseDown={(event) => onRowMouseDown(event, item.id)}
                                            onMouseEnter={() => onRowMouseEnter(item.id)}
                                        >
                                            {
                                                props.bulk?.isActiveIdsSelecting &&
                                                <div className={styles.selectInput}>
                                                    <CheckboxInput
                                                        value={props.bulk.selectedIds.includes(item.id)}
                                                        onChangeValue={() => onToggleId(item.id)}
                                                    />
                                                </div>
                                            }
                                            <Row
                                                className={styles.rowContent}
                                                cellClassName={styles.cell}
                                                gridStyle={gridStyle}
                                                rowIndex={rowIndex}
                                                item={item}
                                                columnsEnum={props.columnsEnum}
                                                columns={columns}
                                                isMobile={isMobile}
                                                maxRowWidthDesktop={props.maxRowWidthDesktop ?? null}
                                                maxRowWidthMobile={props.maxRowWidthMobile ?? null}
                                                formatColumnOption={props.formatColumnOption}
                                            />
                                        </div>
                                    );
                                })
                            }
                        </RowsWrapper> :
                        <EmptyResult/> :
                    <Loading className={styles.loading}/>
            }
            {
                !!props.bulk?.isActiveIdsSelecting &&
                props.items &&
                <BulkActionBar
                    selectedCount={props.bulk.selectedIds.length}
                    totalCount={props.items.length}
                    actions={props.bulk.actions}
                    description={props.bulk.description ?? null}
                    onCancel={props.bulk.onCancel}
                />
            }
        </div>
    );
};

export default EntityTable;
